import React, { useRef, useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Paper,
  Alert,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import CryptoJS from "crypto-js";
import { useGoogleLogin } from "@react-oauth/google";
import { verifyCredentials, getUserByEmail } from "../../services/sheetsService";

const GOOGLE_SCOPES = [
  "openid",
  "email",
  "profile",
  "https://www.googleapis.com/auth/spreadsheets",
].join(" ");

const hashPassword = (password) =>
  CryptoJS.SHA256(password + process.env.REACT_APP_CRYPTO_JS_KEY).toString();

const Login = ({ isMobile }) => {
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const pendingCredentials = useRef(null);
  const navigate = useNavigate();

  // Password login — reads sheet directly using API key, no OAuth needed
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.username || !formData.password) return;
    setError("");
    try {
      const hashedPass = hashPassword(formData.password);
      const user = await verifyCredentials(formData.username, hashedPass);
      if (!user) {
        setError("Invalid email or password.");
        return;
      }
      localStorage.setItem("internToken", `pwd_${Date.now()}`);
      localStorage.setItem("userEmail", user.email);
      localStorage.setItem("isAdmin", String(user.isAdmin));
      navigate("/profile");
    } catch (err) {
      setError("Failed to reach the sheet. Please try again.");
    }
  };

  // Google login — triggers OAuth with Sheets scope
  const triggerGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const accessToken = tokenResponse.access_token;
      try {
        const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const info = await res.json();
        const user = await getUserByEmail(info.email);
        if (!user) {
          setShowRegisterDialog(true);
          return;
        }
        localStorage.setItem("internToken", accessToken);
        localStorage.setItem("userEmail", user.email);
        localStorage.setItem("isAdmin", String(user.isAdmin));
        navigate("/profile");
      } catch (err) {
        setError("Failed to reach the sheet. Please try again.");
      }
    },
    onError: () => setError("Google authentication failed. Please try again."),
    scope: GOOGLE_SCOPES,
    flow: "implicit",
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Container
      maxWidth={isMobile ? "md" : "xs"}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "calc(90vh - 50px)",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Paper
          sx={{
            p: isMobile ? 2 : 3,
            flex: 1,
            boxShadow: 3,
            borderRadius: 2,
            m: isMobile ? 2 : "auto",
          }}
        >
          <Typography variant="h5" align="center" gutterBottom>
            Login
          </Typography>

          {error && (
            <Alert
              severity="error"
              action={
                <IconButton size="small" onClick={() => setError("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              name="username"
              variant="outlined"
              margin="normal"
              value={formData.username}
              onChange={handleChange}
              required
              size="small"
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              variant="outlined"
              margin="normal"
              value={formData.password}
              onChange={handleChange}
              required
              size="small"
              sx={{ mt: isMobile ? 0 : "auto" }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: isMobile ? 0 : 2 }}
            >
              Login
            </Button>
          </form>

          <Divider sx={{ my: 2 }}>or</Divider>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setError("");
                pendingCredentials.current = null;
                triggerGoogleLogin();
              }}
              startIcon={
                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="Google"
                  style={{ width: 18, height: 18 }}
                />
              }
            >
              Sign in with Google
            </Button>
          </Box>
        </Paper>
      </Box>

      <Dialog open={showRegisterDialog} onClose={() => setShowRegisterDialog(false)}>
        <DialogTitle>Account Not Found</DialogTitle>
        <DialogContent>
          <DialogContentText>
            No account was found for this Google account. Would you like to register?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRegisterDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => navigate("/register")}>
            Register
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login;